import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ClientConfig } from '../types';

/* ─── Add Client ─── */
interface AddClientDialogProps {
	open: boolean;
	onClose: () => void;
}

export function AddClientDialog({ open, onClose }: AddClientDialogProps) {
	const [saved, setSaved] = useState(false);
	const [form, setForm] = useState({
		clientId: '',
		name: '',
		email: '',
		status: 'Active',
		description: '',
	});
	const valid = form.clientId && form.name && form.email && form.description;

	if (saved) {
		return (
			<Dialog open={open} onOpenChange={onClose}>
				<DialogContent className="sm:max-w-md">
					<div className="flex flex-col items-center gap-4 py-4">
						<CheckCircle2 className="h-8 w-8 text-green-500" />
						<div className="text-center">
							<h3 className="text-lg font-semibold">
								Client configuration created
							</h3>
							<p className="text-sm text-muted-foreground">
								The client is now ready to use.
							</p>
						</div>
					</div>
					<div className="flex justify-end">
						<Button onClick={onClose}>Done</Button>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	const update = (key: string, value: string) =>
		setForm((f) => ({ ...f, [key]: value }));

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>Create Client Configuration</DialogTitle>
					<DialogDescription>
						Create a new client configuration to start sending notifications.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>
								Client ID <span className="text-red-500">*</span>
							</Label>
							<Input
								placeholder="client_id"
								value={form.clientId}
								onChange={(e) => update('clientId', e.target.value)}
								className="mt-1"
							/>
						</div>
						<div>
							<Label>
								Name <span className="text-red-500">*</span>
							</Label>
							<Input
								placeholder="Client Name"
								value={form.name}
								onChange={(e) => update('name', e.target.value)}
								className="mt-1"
							/>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>
								From Email <span className="text-red-500">*</span>
							</Label>
							<Input
								placeholder="noreply@yourdomain.com"
								value={form.email}
								onChange={(e) => update('email', e.target.value)}
								className="mt-1"
							/>
						</div>
						<div>
							<Label>Status</Label>
							<Select
								value={form.status}
								onValueChange={(v) => update('status', v)}
							>
								<SelectTrigger className="mt-1">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Active">Active</SelectItem>
									<SelectItem value="Inactive">Inactive</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<div>
						<Label>
							Description <span className="text-red-500">*</span>
						</Label>
						<Textarea
							placeholder="Enter description"
							rows={4}
							value={form.description}
							onChange={(e) => update('description', e.target.value)}
							className="mt-1"
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button disabled={!valid} onClick={() => setSaved(true)}>
						Save
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/* ─── View Client ─── */
interface ViewClientDialogProps {
	client: ClientConfig;
	open: boolean;
	onClose: () => void;
}

export function ViewClientDialog({
	client,
	open,
	onClose,
}: ViewClientDialogProps) {
	const rows: [string, string, string][] = [
		['Client ID', client.clientId, 'mono'],
		['Name', client.name, 'text'],
		['Email', client.email, 'text'],
		['Status', client.status, 'badge'],
		['Created At', client.created, 'muted'],
	];

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>View Client</DialogTitle>
				</DialogHeader>
				<div className="space-y-1">
					{rows.map(([key, value, type]) => (
						<div
							key={key}
							className="flex items-center border-b py-2.5 text-sm"
						>
							<span className="w-32 shrink-0 text-muted-foreground">{key}</span>
							{type === 'badge' ? (
								<Badge variant={value === 'Active' ? 'default' : 'secondary'}>
									{value}
								</Badge>
							) : type === 'mono' ? (
								<span className="font-mono text-sm">{value}</span>
							) : type === 'muted' ? (
								<span className="text-muted-foreground">{value}</span>
							) : (
								<span>{value || 'N/A'}</span>
							)}
						</div>
					))}
				</div>
				<div className="flex justify-end">
					<Button onClick={onClose}>Close</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/* ─── Edit Client ─── */
interface EditClientDialogProps {
	client: ClientConfig;
	open: boolean;
	onClose: () => void;
}

export function EditClientDialog({
	client,
	open,
	onClose,
}: EditClientDialogProps) {
	const [form, setForm] = useState({
		name: client.name,
		email: client.email,
		status: client.status,
	});

	const update = (key: string, value: string) =>
		setForm((f) => ({ ...f, [key]: value }));

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						Edit Client <span className="font-mono">{client.clientId}</span>
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<Label>Client ID (read only)</Label>
						<Input value={client.clientId} disabled className="mt-1 bg-muted" />
					</div>
					<div>
						<Label>Name</Label>
						<Input
							placeholder="Client Name"
							value={form.name}
							onChange={(e) => update('name', e.target.value)}
							className="mt-1"
						/>
					</div>
					<div>
						<Label>Status</Label>
						<Select
							value={form.status}
							onValueChange={(v) => update('status', v)}
						>
							<SelectTrigger className="mt-1">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Active">Active</SelectItem>
								<SelectItem value="Inactive">Inactive</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div>
						<Label>Email</Label>
						<Input
							placeholder="Email address"
							value={form.email}
							onChange={(e) => update('email', e.target.value)}
							className="mt-1"
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={onClose}>Save changes</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
